import { type SubmitEvent, useState } from 'react';
import { AddressElement, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeCardElementOptions } from '@stripe/stripe-js';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { BillingAddress } from '@/hooks/useBilling';

interface PaymentMethodFormProps {
  /** Called once the card has been tokenized into a Stripe PaymentMethod.
   * The `stripe` instance is forwarded so callers can chain
   * `confirmCardPayment` when the server returns a PaymentIntent
   * client_secret (e.g., SCA).
   *
   * `address` is present only when `collectBillingAddress` is set. */
  onSuccess: (
    paymentMethodId: string,
    stripe: Stripe,
    address?: BillingAddress,
  ) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
  /** Collect a billing address alongside the card.
   *
   * Off by default: only the flow that CREATES the Stripe customer needs one,
   * and that is where automatic tax reads its location from. Attaching another
   * card to an existing customer does not, and asking again there would imply
   * the address is per-card when Stripe holds it per-customer. */
  collectBillingAddress?: boolean;
}

// Stripe's CardElement renders inside an iframe; styling has to be
// passed in via the SDK rather than CSS. Color/font keep visual
// parity with the surrounding form when in light mode. Dark-mode
// styling for the iframe would require an additional Stripe config
// pass — out of scope for this migration.
const cardElementOptions: StripeCardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, sans-serif',
    },
    invalid: { color: '#9e2146' },
  },
  hidePostalCode: false,
};

export default function PaymentMethodForm({
  onSuccess,
  onCancel,
  submitLabel = 'Add payment method',
  isLoading: externalLoading = false,
  collectBillingAddress = false,
}: PaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }
    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found.');
      setProcessing(false);
      return;
    }

    // Read the address BEFORE tokenizing the card. AddressElement.getValue()
    // runs its own validation, and failing here costs nothing — failing after
    // createPaymentMethod would leave an orphaned PaymentMethod behind on every
    // incomplete address.
    let address: BillingAddress | undefined;
    if (collectBillingAddress) {
      const addressElement = elements.getElement(AddressElement);
      if (!addressElement) {
        setError('Address element not found.');
        setProcessing(false);
        return;
      }
      const { complete, value } = await addressElement.getValue();
      if (!complete) {
        setError('Please complete your billing address.');
        setProcessing(false);
        return;
      }
      address = {
        line1: value.address.line1,
        line2: value.address.line2 ?? undefined,
        city: value.address.city,
        state: value.address.state || undefined,
        postal_code: value.address.postal_code,
        country: value.address.country,
      };
    }

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      if (stripeError) {
        setError(stripeError.message ?? 'An error occurred while processing your card.');
        setProcessing(false);
        return;
      }
      try {
        await onSuccess(paymentMethod.id, stripe, address);
      } catch (callbackErr) {
        setError(
          callbackErr instanceof Error ? callbackErr.message : 'Failed to complete payment.',
        );
        setProcessing(false);
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  const isDisabled = !stripe || processing || externalLoading;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(e);
      }}
      className="space-y-4"
    >
      <div className="space-y-1.5">
        <Label>Card information</Label>
        <div className="rounded-md border border-border bg-card px-3 py-3">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {collectBillingAddress ? (
        <div className="space-y-1.5">
          <Label>Billing address</Label>
          <AddressElement options={{ mode: 'billing' }} />
        </div>
      ) : null}

      <div className="space-y-1.5">
        {error ? (
          <p className="text-xs text-destructive inline-flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={isDisabled}>
          {processing || externalLoading ? 'Processing…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={processing || externalLoading}
          >
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
